"use client";
import { motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";

const CounterSection = () => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [startCount, setStartCount] = useState(false);

  const stats = [
    { number: 50, label: "Projects" },
    { number: 10, label: "Countries Served" },
    { number: 25, label: "Years of Experience" }
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setStartCount(true);
            observer.disconnect(); // Stop observing once triggered
          }
        });
      },
      { threshold: 0.3 } // Trigger when 30% of the section is visible
    );

    if (ref.current) observer.observe(ref.current);

    return () => observer.disconnect();
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.2, delayChildren: 0.3 }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { duration: 0.6, ease: "easeOut" } }
  };

  return (
    <section className="py-16 bg-gradient-to-br from-gray-800 via-sky-600 to-gray-800">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          ref={ref}
          initial="hidden"
          animate={startCount ? "visible" : "hidden"}
          variants={containerVariants}
          className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center"
        >
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              className="bg-white backdrop-blur-sm rounded-xl p-8 border border-white/20"
            >
              <div className="text-5xl font-bold text-sky-900 mb-2">
                {startCount ? <Counter target={stat.number} /> : 0}
                <span className="text-sky-900">+</span>
              </div>
              <p className="text-xl text-gray-900 font-bold">{stat.label}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

const Counter = ({ target }: { target: number }) => {
  const [count, setCount] = useState(0);
  const duration = 2; // seconds

  useEffect(() => {
    let start = 0;
    const increment = target / (duration * 60); // 60fps

    const counter = setInterval(() => {
      start += increment;
      if (start >= target) {
        setCount(target);
        clearInterval(counter);
      } else {
        setCount(Math.floor(start));
      }
    }, 1000 / 60);

    return () => clearInterval(counter);
  }, [target]);

  return <span>{count}</span>;
};

export default CounterSection;
